import { useState } from 'react';
import { api, dateTimeFmt, phoneFmt } from '../api';
import { useApi } from '../useApi';
import { Empty, ErrorBox, Loading, useToast } from '../ui';

type Device = {
  id: string;
  label: string;
  role: string;
  phone: string | null;
  platform: string;
  appVersion: string | null;
  batteryPct: number | null;
  lastSeenAt: string | null;
  online: boolean;
  dailyLimit: number;
  sentToday: number;
  active: boolean;
  user: { id: string; name: string; role: string } | null;
};

const ROLE_RU: Record<string, string> = {
  SMS_GATEWAY: 'SMS-шлюз',
  CALL_SOURCE: 'Журнал звонков',
  DRIVER_APP: 'Телефон водителя',
  TEST: 'Тестовый',
};

const ROLE_HINT: Record<string, string> = {
  SMS_GATEWAY: 'Отправляет коды и уведомления со своей SIM. Экономит деньги на агрегаторе.',
  CALL_SOURCE: 'Отдаёт журнал звонков, по нему всплывает карточка клиента.',
  DRIVER_APP: 'Маршрутный лист, подпись клиента, работа без связи.',
  TEST: 'Стенд: и шлюз, и звонки, и водитель на одном аппарате.',
};

/**
 * Телефоны компании, подключённые к системе.
 *
 * Экран нужен ровно для одного: понимать, живы ли телефоны. Если SMS-шлюз
 * офлайн — коды на вход не уходят, и узнать об этом надо раньше, чем позвонит
 * первый клиент, а не после.
 */
export function Devices() {
  const { data, error, loading, reload } = useApi<Device[]>('/devices');
  const sms = useApi<Array<{ id: string; phone: string; text: string; status: string; createdAt: string; device: { label: string } | null }>>('/admin/sms?limit=20');
  const [issued, setIssued] = useState<{ token: string; label: string } | null>(null);
  const toast = useToast();

  const add = async (form: { label: string; role: string; phone: string; dailyLimit: number }) => {
    const res = await api<{ token: string; device: { label: string } }>('/devices', { body: form });
    setIssued({ token: res.token, label: res.device.label });
    await reload();
  };

  return (
    <>
      <h1>Телефоны</h1>

      {issued ? (
        <div className="card" style={{ borderColor: 'var(--blue)' }}>
          <h2>Токен для «{issued.label}»</h2>
          <p className="muted">
            Показывается один раз. Введите его в приложении-шлюзе на телефоне — или отсканируйте QR.
            Потерялся — выпустите новый и отзовите старый.
          </p>
          <code style={{ display: 'block', padding: 10, background: '#f4f7fb', borderRadius: 8, wordBreak: 'break-all' }}>
            {issued.token}
          </code>
          <div className="row" style={{ marginTop: 10 }}>
            <button
              onClick={() => {
                void navigator.clipboard?.writeText(issued.token);
                toast.show('Токен скопирован');
              }}
            >
              Скопировать
            </button>
            <button className="ghost" onClick={() => setIssued(null)}>
              Я записал, скрыть
            </button>
          </div>
        </div>
      ) : null}

      <div className="card">
        {loading ? (
          <Loading />
        ) : error ? (
          <ErrorBox error={error} />
        ) : !data?.length ? (
          <Empty>Телефоны не подключены. Добавьте первый — это шлюз для SMS.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Название</th>
                <th>Роль</th>
                <th>SIM</th>
                <th>Связь</th>
                <th>Заряд</th>
                <th className="num">SMS сегодня</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.id}>
                  <td>
                    <b>{d.label}</b>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {d.user?.name ?? ''} {d.appVersion ? `· v${d.appVersion}` : ''}
                    </div>
                  </td>
                  <td>
                    <span className="badge blue">{ROLE_RU[d.role] ?? d.role}</span>
                  </td>
                  <td className="mono">{phoneFmt(d.phone)}</td>
                  <td>
                    {!d.active ? (
                      <span className="badge red">отозван</span>
                    ) : d.online ? (
                      <span className="badge green">на связи</span>
                    ) : (
                      <span className="badge amber">молчит с {dateTimeFmt(d.lastSeenAt)}</span>
                    )}
                  </td>
                  <td>{d.batteryPct !== null ? `${d.batteryPct}%` : '—'}</td>
                  <td className="num">
                    {d.sentToday} / {d.dailyLimit}
                  </td>
                  <td>
                    {d.active ? (
                      <button
                        className="ghost"
                        onClick={async () => {
                          if (!confirm(`Отозвать токен «${d.label}»? Телефон перестанет работать.`)) return;
                          await api(`/devices/${d.id}/revoke`, { body: {} });
                          toast.show('Токен отозван');
                          await reload();
                        }}
                      >
                        отозвать
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AddDevice onAdd={add} />

      <div className="card">
        <h2>Последние SMS</h2>
        {!sms.data?.length ? (
          <Empty>Сообщений пока не было</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Когда</th>
                <th>Кому</th>
                <th>Текст</th>
                <th>Через</th>
                <th>Состояние</th>
              </tr>
            </thead>
            <tbody>
              {sms.data.map((m) => (
                <tr key={m.id}>
                  <td className="muted">{dateTimeFmt(m.createdAt)}</td>
                  <td className="mono">{phoneFmt(m.phone)}</td>
                  {/* Код внутри текста не показываем целиком: экран видят все,
                      кто проходит мимо рабочего места */}
                  <td className="muted">{m.text.replace(/\d{4,}/g, '••••')}</td>
                  <td className="muted">{m.device?.label ?? '—'}</td>
                  <td>
                    <span className={`badge ${m.status === 'FAILED' ? 'red' : m.status === 'QUEUED' ? 'amber' : 'green'}`}>
                      {m.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {toast.node}
    </>
  );
}

function AddDevice({ onAdd }: { onAdd: (f: { label: string; role: string; phone: string; dailyLimit: number }) => Promise<void> }) {
  const [form, setForm] = useState({ label: '', role: 'SMS_GATEWAY', phone: '', dailyLimit: 100 });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="card"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          await onAdd(form);
          setForm({ label: '', role: 'SMS_GATEWAY', phone: '', dailyLimit: 100 });
        } catch (e) {
          setError(e);
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2>Подключить телефон</h2>
      <div className="row">
        <div className="field" style={{ flex: '2 1 240px' }}>
          <label>Название (чтобы узнать аппарат)</label>
          <input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Офис-1, Redmi 12"
            required
          />
        </div>
        <div className="field" style={{ flex: '1 1 200px' }}>
          <label>Роль</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {Object.entries(ROLE_RU).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: '1 1 180px' }}>
          <label>Номер SIM</label>
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+7 …" />
        </div>
        <div className="field" style={{ flex: '0 0 150px' }}>
          <label>Лимит SMS в сутки</label>
          <input
            type="number"
            min={1}
            value={form.dailyLimit}
            onChange={(e) => setForm({ ...form, dailyLimit: Number(e.target.value) })}
          />
        </div>
        <div className="field" style={{ alignSelf: 'flex-end' }}>
          <button className="primary" disabled={busy}>
            Выпустить токен
          </button>
        </div>
      </div>
      <div className="muted">{ROLE_HINT[form.role]}</div>
      <ErrorBox error={error} />
    </form>
  );
}
